'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { useCurrentBusiness } from '@/lib/business-context';
import { haptics } from '@/lib/native';
import {
  addPushListeners,
  enablePush,
  isPushSupported,
  notificationPath,
  pushPermission,
  registerIfGranted,
} from '@/lib/native/push';
import { PushPromptSheet } from './push-prompt-sheet';

/** "Not now" buys the user a week of quiet. */
const SNOOZE_KEY = 'sweetspot_push_prompt_snoozed_until';
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Screens where notifications are obviously about *this* — someone is waiting
 * on a reply, an offer, an order. The marketplace is deliberately absent: it is
 * the cold-start screen, and a permission sheet on launch is how apps get
 * denied.
 */
const PROMPT_PATHS = [
  '/messages',
  '/buy/offers',
  '/buy/orders',
  '/orders',
  '/sell/offers',
  '/dashboard',
  '/settings',
];

/** Let the screen settle before sliding a sheet over it. */
const PROMPT_DELAY_MS = 900;

function snoozed(): boolean {
  try {
    const until = Number(localStorage.getItem(SNOOZE_KEY) || 0);
    return Number.isFinite(until) && until > Date.now();
  } catch {
    return false;
  }
}

function snooze(): void {
  try {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
  } catch {
    /* storage unavailable — the sheet reappears next session, which is fine */
  }
}

/**
 * Push wiring for the native shell. Mounted once inside the signed-in app.
 *
 *  - re-registers the APNs token on every cold start (tokens rotate);
 *  - turns a foreground push into a toast with a View action, because iOS shows
 *    nothing at all while the app is open;
 *  - routes a tapped notification to `data.url`;
 *  - runs the soft pre-prompt on the screens where it makes sense.
 *
 * Renders nothing outside the app: every helper it calls is a no-op on the web.
 */
export function PushProvider() {
  const router = useRouter();
  const pathname = usePathname();
  const { currentBusinessId } = useCurrentBusiness();
  const [promptOpen, setPromptOpen] = useState(false);
  const askedThisSession = useRef(false);

  // Cold start: refresh the token whenever permission is already granted.
  // Gated on a resolved business because the marketplace answers signed-out
  // visitors too, and an unauthenticated PUT would just 401.
  useEffect(() => {
    if (!isPushSupported() || !currentBusinessId) return;
    void registerIfGranted();
  }, [currentBusinessId]);

  useEffect(() => {
    if (!isPushSupported()) return;
    return addPushListeners({
      onReceived: (payload) => {
        const path = notificationPath(payload);
        void haptics.light();
        toast(payload.title || 'Sweetspot', {
          description: payload.body,
          action: path
            ? {
                label: 'View',
                onClick: () => {
                  void haptics.selection();
                  router.push(path);
                },
              }
            : undefined,
        });
      },
      onAction: (payload) => {
        const path = notificationPath(payload);
        if (path) router.push(path);
      },
    });
  }, [router]);

  // Soft pre-prompt. Deliberately not on first paint: it waits for a screen
  // where the value proposition is on-screen behind the sheet.
  useEffect(() => {
    if (!isPushSupported() || !currentBusinessId) return;
    if (askedThisSession.current || snoozed()) return;
    if (!PROMPT_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return;

    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    void (async () => {
      // Only ever shown to someone iOS has not asked yet — a denial can only be
      // undone in Settings, and a grant needs no sales pitch.
      const state = await pushPermission();
      if (cancelled || (state !== 'prompt' && state !== 'prompt-with-rationale')) return;
      timer = setTimeout(() => {
        if (cancelled) return;
        askedThisSession.current = true;
        setPromptOpen(true);
      }, PROMPT_DELAY_MS);
    })();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [pathname, currentBusinessId]);

  const handleEnable = useCallback(async () => {
    const state = await enablePush();
    setPromptOpen(false);
    if (state === 'granted') {
      void haptics.success();
      toast.success('Notifications on', {
        description: 'Offers, messages and orders will come to this phone.',
      });
      return;
    }
    // A denial is permanent from in-app; say where it lives instead of retrying.
    snooze();
    toast('Notifications are off', {
      description: 'Turn them on in iOS Settings › Sweetspot › Notifications.',
    });
  }, []);

  const handleDismiss = useCallback(() => {
    snooze();
    setPromptOpen(false);
  }, []);

  return (
    <PushPromptSheet open={promptOpen} onEnable={handleEnable} onDismiss={handleDismiss} />
  );
}
